import React, { useEffect, useMemo, useState } from "react";
import {
  Activity,
  ArrowUpRight,
  BadgeDollarSign,
  Building2,
  CalendarClock,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  CreditCard,
  DollarSign,
  Gavel,
  Inbox,
  Loader2,
  LockKeyhole,
  LogOut,
  PieChart,
  Route,
  ShieldCheck,
  Sparkles,
  Truck,
  Users,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { fmtDateTime, fmtMoney, ROLE_LABEL, STATUS_LABEL } from "../lib/format";
import { COMPANY_FULL, COMPANY_LOGO } from "../lib/brand";

const emptyDashboard = {
  summary: {},
  analytics: {
    load_status: {},
    request_status: {},
    plan_distribution: {},
    subscription_status: {},
    company_type: {},
  },
  users: [],
  company_revenue: [],
  company_payments: [],
  recent_loads: [],
  recent_intake_requests: [],
  recent_payments: [],
  recent_payment_transactions: [],
  recent_activity: [],
};

const statusBadge = (status) => `badge ${status || "pending"}`;
const compactNumber = (value) => new Intl.NumberFormat("en-US").format(Number(value || 0));

function normalizeDashboard(data) {
  return {
    ...emptyDashboard,
    ...data,
    summary: data?.summary || {},
    analytics: { ...emptyDashboard.analytics, ...(data?.analytics || {}) },
    users: data?.users || [],
    company_revenue: data?.company_revenue || [],
    company_payments: data?.company_payments || [],
    recent_loads: data?.recent_loads || [],
    recent_intake_requests: data?.recent_intake_requests || [],
    recent_payments: data?.recent_payments || [],
    recent_payment_transactions: data?.recent_payment_transactions || [],
    recent_activity: data?.recent_activity || [],
  };
}

const MetricTile = ({ label, value, detail, icon: Icon, tone = "navy", testId }) => {
  const tones = {
    navy: "bg-navy text-white",
    gold: "bg-gold text-slate-950",
    sky: "bg-sky-600 text-white",
    emerald: "bg-emerald-600 text-white",
    rose: "bg-rose-600 text-white",
    slate: "bg-slate-800 text-white",
  };
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm min-h-[132px]" data-testid={testId}>
      <div className="flex items-start justify-between gap-3">
        <div className="text-[10px] tracking-[0.18em] uppercase text-slate-500 font-bold leading-relaxed">{label}</div>
        <div className={`w-9 h-9 rounded-md flex items-center justify-center ${tones[tone] || tones.navy}`}>
          <Icon size={17} strokeWidth={1.9} />
        </div>
      </div>
      <div className="font-display text-3xl font-bold text-navy tracking-tight mt-4 leading-none">{value}</div>
      {detail && <div className="text-xs text-slate-500 mt-3 leading-relaxed">{detail}</div>}
    </div>
  );
};

function MiniBars({ title, rows, emptyLabel = "No data yet" }) {
  const max = Math.max(...rows.map((row) => row.value), 0);
  return (
    <section className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
      <div className="flex items-center justify-between gap-3 mb-5">
        <h2 className="font-display text-lg font-bold text-navy">{title}</h2>
        <PieChart size={18} className="text-slate-400" />
      </div>
      <div className="space-y-4">
        {rows.length === 0 && <div className="text-sm text-slate-400">{emptyLabel}</div>}
        {rows.map((row) => (
          <div key={row.label}>
            <div className="flex items-center justify-between gap-3 text-sm mb-1.5">
              <span className="font-semibold text-slate-700 capitalize">{row.label.replaceAll("_", " ")}</span>
              <span className="font-mono text-xs text-slate-500">{compactNumber(row.value)}</span>
            </div>
            <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
              <div className={`h-full rounded-full ${row.color}`} style={{ width: `${max ? Math.max((row.value / max) * 100, 8) : 0}%` }} />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function SuperAdminLogin({ onSignedIn, currentUser }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/superadmin-login", { email, password });
      await onSignedIn();
    } catch (err) {
      setError(err.response?.data?.detail || "Unable to sign in");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="w-full max-w-5xl grid lg:grid-cols-[0.9fr_1.1fr] bg-white border border-slate-200 shadow-xl rounded-lg overflow-hidden">
        <div className="bg-navy text-white p-8 md:p-10 flex flex-col justify-between min-h-[520px]">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-md bg-white flex items-center justify-center overflow-hidden">
              <img src={COMPANY_LOGO} alt={COMPANY_FULL} className="w-full h-full object-contain" />
            </div>
            <div>
              <div className="font-display font-bold text-lg leading-tight">{COMPANY_FULL}</div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-gold font-bold mt-0.5">Superadmin</div>
            </div>
          </div>
          <div>
            <div className="w-12 h-12 rounded-md bg-white/10 flex items-center justify-center mb-5">
              <ShieldCheck size={24} />
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tighter leading-tight">
              Superadmin Portal
            </h1>
            <p className="text-slate-300 mt-4 max-w-sm">
              Sign in with your admin email and password to review platform revenue, users, companies, loads, and activity.
            </p>
          </div>
          <div className="text-xs text-slate-400">Restricted access for platform owners.</div>
        </div>

        <div className="p-8 md:p-12 flex items-center">
          <form onSubmit={submit} className="w-full max-w-md mx-auto" data-testid="superadmin-login-form">
            <div className="flex items-center gap-2 text-navy mb-3">
              <LockKeyhole size={18} />
              <span className="text-[11px] tracking-[0.2em] uppercase font-bold">Credentials</span>
            </div>
            <h2 className="font-display text-3xl font-bold text-navy tracking-tight mb-2">Sign in</h2>
            <p className="text-slate-500 mb-7">
              {currentUser
                ? `${currentUser.email} is signed in, but is not a super admin account. Enter the configured superadmin credentials to switch.`
                : "Use the configured superadmin account."}
            </p>

            <label className="block text-sm font-semibold text-slate-700 mb-2" htmlFor="superadmin-email">Email</label>
            <input
              id="superadmin-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              autoComplete="username"
              className="input-base w-full mb-4"
              data-testid="superadmin-email"
              required
            />

            <label className="block text-sm font-semibold text-slate-700 mb-2" htmlFor="superadmin-password">Password</label>
            <input
              id="superadmin-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              type="password"
              autoComplete="current-password"
              className="input-base w-full mb-5"
              data-testid="superadmin-password"
              required
            />

            {error && <div className="text-sm font-semibold text-red-600 mb-4" data-testid="superadmin-login-error">{error}</div>}

            <button type="submit" disabled={busy} className="btn-primary w-full justify-center py-3" data-testid="superadmin-login-submit">
              {busy ? <><Loader2 size={16} className="animate-spin" /> Signing in</> : "Enter dashboard"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

const dateOnly = (value) => (value ? String(value).slice(0, 10) : "Not provided");
const showValue = (value) => {
  if (value === 0) return "0";
  return value || "Not provided";
};

function DetailField({ label, value, mono = false }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] tracking-[0.14em] uppercase text-slate-400 font-bold leading-relaxed">{label}</div>
      <div className={`text-sm text-slate-700 mt-1 break-words ${mono ? "font-mono text-xs" : "font-semibold"}`}>
        {showValue(value)}
      </div>
    </div>
  );
}

function RequestsPanel({ requests }) {
  const [openRequestId, setOpenRequestId] = useState(null);

  return (
    <section className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden" data-testid="superadmin-request-inbox">
      <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-bold text-navy">Full load request forms</h2>
          <p className="text-xs text-slate-500 mt-0.5">Complete submissions from logged-in users and public shippers.</p>
        </div>
        <Inbox size={18} className="text-slate-400" />
      </div>
      <div className="divide-y divide-slate-100 max-h-[720px] overflow-y-auto">
        {requests.length === 0 && <div className="p-6 text-slate-400 text-sm">No load requests yet.</div>}
        {requests.map((row) => {
          const isOpen = openRequestId === row.intake_id;
          return (
            <div key={row.intake_id} data-testid={`superadmin-intake-${row.intake_id}`}>
              <button
                type="button"
                onClick={() => setOpenRequestId(isOpen ? null : row.intake_id)}
                className="w-full p-4 text-left hover:bg-slate-50 transition-colors"
                data-testid={`superadmin-intake-toggle-${row.intake_id}`}
                aria-expanded={isOpen}
              >
                <div className="grid md:grid-cols-[1.1fr_1.3fr_0.8fr_auto] gap-3 md:items-center">
                  <div className="min-w-0">
                    <div className="font-semibold text-navy truncate">{row.contact_name || "Unknown shipper"}</div>
                    <div className="text-xs text-slate-500 truncate">
                      {row.intake_id} · {row.source || "unknown"} · {dateOnly(row.created_at)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-700 min-w-0">
                    <Route size={14} className="text-slate-400 flex-shrink-0" />
                    <span className="truncate">{row.pickup_city}, {row.pickup_state} to {row.dropoff_city}, {row.dropoff_state}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-slate-400">Target</span>
                      <div className="font-semibold text-slate-700">{fmtMoney(row.target_rate || 0)}</div>
                    </div>
                    <div>
                      <span className="text-slate-400">Equip</span>
                      <div className="font-semibold text-slate-700 capitalize">{row.equipment_type || "n/a"}</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between md:justify-end gap-3">
                    <span className={statusBadge(row.status)}>{row.status || "pending"}</span>
                    <span className="w-8 h-8 rounded-md border border-slate-200 flex items-center justify-center text-slate-500 bg-white">
                      {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </span>
                  </div>
                </div>
              </button>

              {isOpen && (
                <div className="px-5 pb-5 bg-slate-50/70" data-testid={`superadmin-intake-details-${row.intake_id}`}>
                  <div className="bg-white border border-slate-200 rounded-lg p-5">
                    <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-x-5 gap-y-4">
                      <DetailField label="Contact email" value={row.contact_email} />
                      <DetailField label="Contact phone" value={row.contact_phone} />
                      <DetailField label="Submitted by" value={row.submitted_by_user_id || "Public request"} mono />
                      <DetailField label="Company" value={row.company_id || "No company"} mono />
                      <DetailField label="Created date" value={dateOnly(row.created_at)} />
                      <DetailField label="Reviewed by" value={row.reviewed_by || "Not reviewed"} />
                    </div>

                    <div className="mt-5 grid lg:grid-cols-2 gap-5">
                      <div className="border border-slate-200 rounded-lg p-4">
                        <div className="text-[10px] tracking-[0.16em] uppercase text-slate-500 font-bold mb-3">Pickup</div>
                        <div className="grid sm:grid-cols-2 gap-4">
                          <DetailField label="Address" value={row.pickup_address} />
                          <DetailField label="City/state/zip" value={`${showValue(row.pickup_city)}, ${showValue(row.pickup_state)} ${showValue(row.pickup_zip)}`} />
                          <DetailField label="Ready date" value={dateOnly(row.pickup_date)} />
                          <DetailField label="Distance" value={row.distance_mi ? `${row.distance_mi} mi` : "Not calculated"} />
                        </div>
                      </div>
                      <div className="border border-slate-200 rounded-lg p-4">
                        <div className="text-[10px] tracking-[0.16em] uppercase text-slate-500 font-bold mb-3">Dropoff</div>
                        <div className="grid sm:grid-cols-2 gap-4">
                          <DetailField label="Address" value={row.dropoff_address} />
                          <DetailField label="City/state/zip" value={`${showValue(row.dropoff_city)}, ${showValue(row.dropoff_state)} ${showValue(row.dropoff_zip)}`} />
                          <DetailField label="Deliver by" value={dateOnly(row.delivery_date)} />
                          <DetailField label="Transit estimate" value={row.eta_label} />
                        </div>
                      </div>
                    </div>

                    <div className="mt-5 grid sm:grid-cols-2 xl:grid-cols-5 gap-x-5 gap-y-4">
                      <DetailField label="Equipment" value={row.equipment_type} />
                      <DetailField label="Weight" value={row.weight ? `${row.weight} lbs` : "Not provided"} />
                      <DetailField label="Commodity" value={row.commodity} />
                      <DetailField label="Target rate" value={fmtMoney(row.target_rate || 0)} />
                      <DetailField label="Created load" value={row.load_id || "No load yet"} mono />
                    </div>

                    {(row.notes || row.rejection_reason || row.reviewed_at) && (
                      <div className="mt-5 grid md:grid-cols-3 gap-4">
                        <DetailField label="Notes" value={row.notes} />
                        <DetailField label="Rejection reason" value={row.rejection_reason} />
                        <DetailField label="Reviewed at" value={dateOnly(row.reviewed_at)} />
                      </div>
                    )}

                    <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between gap-3 text-xs text-slate-500">
                      <span>Request source: <b className="text-slate-700">{row.source || "unknown"}</b></span>
                      <span>{row.status === "approved" && row.load_id ? `Converted to load ${row.load_id}` : "Awaiting admin workflow"}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function PaymentsPanel({ payments }) {
  return (
    <section className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden" data-testid="superadmin-payment-ledger">
      <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-bold text-navy">Payment ledger</h2>
          <p className="text-xs text-slate-500 mt-0.5">Recent paid invoices, subscriptions, and credit purchases.</p>
        </div>
        <CreditCard size={18} className="text-slate-400" />
      </div>
      <div className="overflow-x-auto">
        <table className="table-base">
          <thead><tr><th>Date</th><th>Company</th><th>Plan</th><th>Status</th><th>Amount</th></tr></thead>
          <tbody>
            {payments.length === 0 && <tr><td colSpan={5} className="text-center text-slate-400 py-10">No payments yet.</td></tr>}
            {payments.map((row) => (
              <tr key={row.invoice_id || `${row.company_id}-${row.created_at}`} data-testid={`superadmin-payment-${row.invoice_id || row.company_id}`}>
                <td className="text-xs text-slate-500">{row.created_at ? row.created_at.slice(0, 10) : ""}</td>
                <td className="font-mono text-xs">{row.company_id || "n/a"}</td>
                <td>
                  <div className="font-semibold text-navy">{row.plan || "payg"}</div>
                  <div className="text-xs text-slate-500">{row.cycle || "one-time"}</div>
                </td>
                <td><span className={statusBadge(row.status)}>{row.status || "unknown"}</span></td>
                <td className="font-mono font-semibold text-navy">{fmtMoney(row.amount || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function SuperAdminDashboard({ user, dashboard, loading, logout }) {
  const summary = dashboard.summary || {};
  const analytics = dashboard.analytics || emptyDashboard.analytics;
  const barRows = useMemo(() => {
    const colors = ["bg-gold", "bg-sky-500", "bg-emerald-500", "bg-rose-500", "bg-slate-500", "bg-indigo-500"];
    const toRows = (obj) => Object.entries(obj || {}).map(([label, value], index) => ({
      label,
      value: Number(value || 0),
      color: colors[index % colors.length],
    }));
    return {
      loadStatus: toRows(analytics.load_status),
      requestStatus: toRows(analytics.request_status),
      planDistribution: toRows(analytics.plan_distribution),
    };
  }, [analytics]);

  const topCompany = dashboard.company_revenue[0];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-[1500px] mx-auto px-4 md:px-6 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-md bg-white border border-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0">
              <img src={COMPANY_LOGO} alt={COMPANY_FULL} className="w-full h-full object-contain" />
            </div>
            <div className="min-w-0">
              <div className="font-display text-lg font-bold text-navy leading-tight truncate">Superadmin Analytics</div>
              <div className="text-xs text-slate-500 truncate">{user.email}</div>
            </div>
          </div>
          <button onClick={logout} className="btn-secondary py-2 px-3 text-sm" data-testid="superadmin-logout">
            <LogOut size={15} /> Logout
          </button>
        </div>
      </header>

      <main className="max-w-[1500px] mx-auto p-4 md:p-6 space-y-6" data-testid="superadmin-dashboard">
        <section className="bg-navy rounded-lg p-5 md:p-6 text-white shadow-sm">
          <div className="flex items-start justify-between gap-5 flex-wrap">
            <div className="max-w-3xl">
              <div className="text-[11px] tracking-[0.2em] uppercase text-gold font-bold mb-2">Platform command center</div>
              <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight leading-none">Real-time users, requests, plans, and payments</h1>
              <p className="text-slate-300 mt-4 max-w-2xl">
                Every number comes from the live app collections: users, companies, load intake, loads, bids, payment logs, and checkout transactions.
              </p>
            </div>
            <div className="flex items-center gap-3 bg-white/10 border border-white/15 rounded-lg px-4 py-3 min-w-[240px]">
              <Sparkles size={18} className="text-gold" />
              <div>
                <div className="text-xs text-slate-300">Top account</div>
                <div className="font-semibold truncate">{topCompany?.name || "No company data"}</div>
              </div>
            </div>
          </div>
          {loading && (
            <div className="mt-5 text-sm text-slate-300 flex items-center gap-2">
              <Loader2 size={16} className="animate-spin" /> Refreshing dashboard
            </div>
          )}
        </section>

        <section className="grid grid-cols-2 xl:grid-cols-6 gap-4">
          <MetricTile label="Payments collected" value={fmtMoney(summary.payments_collected || 0)} detail="Paid platform invoices" icon={BadgeDollarSign} tone="emerald" testId="superadmin-kpi-payments-collected" />
          <MetricTile label="Broker revenue" value={fmtMoney(summary.broker_revenue || 0)} detail={`${fmtMoney(summary.gross_revenue || 0)} gross load value`} icon={DollarSign} tone="gold" testId="superadmin-kpi-broker-revenue" />
          <MetricTile label="Load requests" value={compactNumber(summary.total_load_requests)} detail={`${compactNumber(summary.pending_load_requests)} waiting review`} icon={Inbox} tone="rose" testId="superadmin-kpi-load-requests" />
          <MetricTile label="Users" value={compactNumber(summary.total_users)} detail={`${compactNumber(summary.total_companies)} companies`} icon={Users} tone="sky" testId="superadmin-kpi-total-users" />
          <MetricTile label="Loads" value={compactNumber(summary.total_loads)} detail={`${compactNumber(summary.active_loads)} active, ${compactNumber(summary.delivered_loads)} delivered`} icon={Truck} tone="navy" testId="superadmin-kpi-total-loads" />
          <MetricTile label="Subscriptions" value={compactNumber(summary.active_subscriptions)} detail={`${compactNumber(summary.total_bids)} carrier bids`} icon={ShieldCheck} tone="slate" testId="superadmin-kpi-subscriptions" />
        </section>

        <section className="grid lg:grid-cols-3 gap-4">
          <MiniBars title="Load status" rows={barRows.loadStatus} />
          <MiniBars title="Request status" rows={barRows.requestStatus} />
          <MiniBars title="Plan distribution" rows={barRows.planDistribution} />
        </section>

        <section className="grid xl:grid-cols-[0.9fr_1.1fr] gap-6">
          <RequestsPanel requests={dashboard.recent_intake_requests} />
          <PaymentsPanel payments={dashboard.recent_payments} />
        </section>

        <section className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden" data-testid="superadmin-plan-table">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between gap-3">
            <div>
              <h2 className="font-display text-lg font-bold text-navy">User plans and account value</h2>
              <p className="text-xs text-slate-500 mt-0.5">Company plan, payment date, credits, billing status, and collected amount.</p>
            </div>
            <CalendarClock size={18} className="text-slate-400" />
          </div>
          <div className="overflow-x-auto">
            <table className="table-base">
              <thead><tr><th>Company</th><th>Plan</th><th>Status</th><th>Users</th><th>Credits</th><th>Last payment</th><th>Total paid</th></tr></thead>
              <tbody>
                {dashboard.company_payments.length === 0 && <tr><td colSpan={7} className="text-center text-slate-400 py-10">No company billing data yet.</td></tr>}
                {dashboard.company_payments.map((company) => (
                  <tr key={company.company_id} data-testid={`superadmin-company-payment-${company.company_id}`}>
                    <td>
                      <div className="font-semibold text-navy">{company.company_name}</div>
                      <div className="text-xs text-slate-500">{company.company_type} · {company.company_id}</div>
                    </td>
                    <td>
                      <div className="font-semibold text-navy">{company.plan || "payg"}</div>
                      <div className="text-xs text-slate-500">{company.billing_cycle || "no cycle"}</div>
                    </td>
                    <td><span className={statusBadge(company.subscription_status)}>{company.subscription_status || "unknown"}</span></td>
                    <td>{company.user_count}</td>
                    <td>{company.load_credits}</td>
                    <td>
                      <div className="text-sm text-slate-700">{company.last_payment?.created_at ? company.last_payment.created_at.slice(0, 10) : "No payment"}</div>
                      <div className="text-xs text-slate-500">{company.subscription_current_period_end ? `Renews ${company.subscription_current_period_end.slice(0, 10)}` : ""}</div>
                    </td>
                    <td className="font-mono font-semibold text-navy">{fmtMoney(company.total_paid || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="grid xl:grid-cols-[1.2fr_0.8fr] gap-6">
          <section className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between gap-3">
              <h2 className="font-display text-lg font-bold text-navy">Company load revenue</h2>
              <ArrowUpRight size={18} className="text-slate-400" />
            </div>
            <div className="overflow-x-auto">
              <table className="table-base">
                <thead><tr><th>Company</th><th>Plan</th><th>Loads</th><th>Gross</th><th>Broker</th></tr></thead>
                <tbody>
                  {dashboard.company_revenue.map((company) => (
                    <tr key={company.company_id} data-testid={`superadmin-company-${company.company_id}`}>
                      <td>
                        <div className="font-semibold text-navy">{company.name}</div>
                        <div className="text-xs text-slate-500">{company.type} · {company.user_count} users</div>
                      </td>
                      <td>
                        <span className="badge published">{company.plan || "none"}</span>
                        <div className="text-xs text-slate-500 mt-1">{company.subscription_status || "unknown"}</div>
                      </td>
                      <td>{company.total_loads} total · {company.active_loads} active</td>
                      <td className="font-mono font-semibold">{fmtMoney(company.gross_revenue || 0)}</td>
                      <td className="font-mono font-semibold">{fmtMoney(company.broker_revenue || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between gap-3">
              <h2 className="font-display text-lg font-bold text-navy">Recent activity</h2>
              <Activity size={18} className="text-slate-400" />
            </div>
            <div className="divide-y divide-slate-100 max-h-[480px] overflow-y-auto">
              {dashboard.recent_activity.length === 0 && <div className="p-6 text-slate-400 text-sm">No activity yet.</div>}
              {dashboard.recent_activity.map((row) => (
                <div key={row.log_id} className="p-4" data-testid={`superadmin-activity-${row.log_id}`}>
                  <div className="flex items-center justify-between gap-3">
                    <span className="badge accepted">{row.action}</span>
                    <span className="text-xs text-slate-400">{row.created_at ? fmtDateTime(row.created_at) : ""}</span>
                  </div>
                  <div className="text-sm text-slate-700 mt-2">{row.entity} · {row.entity_id}</div>
                  <div className="text-xs text-slate-500 mt-1">{row.user_id || "system"}</div>
                </div>
              ))}
            </div>
          </section>
        </section>

        <section className="grid xl:grid-cols-2 gap-6">
          <section className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between gap-3">
              <h2 className="font-display text-lg font-bold text-navy">Recent loads</h2>
              <ClipboardList size={18} className="text-slate-400" />
            </div>
            <div className="overflow-x-auto">
              <table className="table-base">
                <thead><tr><th>Load</th><th>Route</th><th>Status</th><th>Rate</th></tr></thead>
                <tbody>
                  {dashboard.recent_loads.length === 0 && <tr><td colSpan={4} className="text-center text-slate-400 py-10">No loads yet.</td></tr>}
                  {dashboard.recent_loads.map((row) => (
                    <tr key={row.load_id} data-testid={`superadmin-load-${row.load_id}`}>
                      <td className="font-mono text-xs font-semibold text-navy">{row.load_id}</td>
                      <td>{row.pickup_city}, {row.pickup_state} to {row.dropoff_city}, {row.dropoff_state}</td>
                      <td><span className={statusBadge(row.status)}>{STATUS_LABEL[row.status] || row.status}</span></td>
                      <td className="font-mono font-semibold">{fmtMoney(row.rate || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between gap-3">
              <h2 className="font-display text-lg font-bold text-navy">All users</h2>
              <Users size={18} className="text-slate-400" />
            </div>
            <div className="overflow-x-auto">
              <table className="table-base">
                <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Company</th></tr></thead>
                <tbody>
                  {dashboard.users.map((row) => (
                    <tr key={row.user_id} data-testid={`superadmin-user-${row.user_id}`}>
                      <td className="font-semibold text-navy">{row.name}</td>
                      <td>{row.email}</td>
                      <td><span className="badge assigned">{ROLE_LABEL[row.role] || row.role}</span></td>
                      <td className="font-mono text-xs">{row.company_id || "platform"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </section>
      </main>
    </div>
  );
}

export default function SuperAdminPortal() {
  const { user, loading, refresh, logout } = useAuth();
  const [dashboard, setDashboard] = useState(emptyDashboard);
  const [dashboardLoading, setDashboardLoading] = useState(false);

  useEffect(() => {
    if (!user || user.role !== "super_admin") return;
    setDashboardLoading(true);
    api.get("/superadmin/dashboard")
      .then((r) => setDashboard(normalizeDashboard(r.data)))
      .finally(() => setDashboardLoading(false));
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-500">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading
      </div>
    );
  }

  if (!user) return <SuperAdminLogin onSignedIn={refresh} />;
  if (user.role !== "super_admin") return <SuperAdminLogin onSignedIn={refresh} currentUser={user} />;
  return <SuperAdminDashboard user={user} dashboard={dashboard} loading={dashboardLoading} logout={logout} />;
}
